import { useEffect, useMemo } from 'react';
import { useGetAllCandidatesByBatch } from 'modules/campaign-creation/hooks/useGetAllCandidatesByBatch';
import { CandidateStatus } from 'generated-graphql/types';

export interface StatusCountWithLabel {
  label: string;
  value: CandidateStatus;
}

export interface UseAllCandidatesByBatchResult {
  statusOptions: StatusCountWithLabel[];
  loading: boolean;
  error: string | null;
}

export const useCandidateStatusCountByBatch = (
  candidateBatchId: string | null
): UseAllCandidatesByBatchResult => {
  const {
    refetch: fetchAllCandidates,
    candidates,
    loading: isFetching,
    error: fetchError
  } = useGetAllCandidatesByBatch(candidateBatchId || '', 500);

  useEffect(() => {
    if (candidateBatchId) {
      fetchAllCandidates();
    }
  }, [candidateBatchId]);

  // Create status counts with formatted labels
  const statusOptions: StatusCountWithLabel[] = useMemo(() => {
    const statusLabels: Record<CandidateStatus, string> = {
      [CandidateStatus.ShortListed]: 'Shortlisted',
      [CandidateStatus.Rejected]: 'Rejected',
      [CandidateStatus.Progressing]: 'Progressing',
      [CandidateStatus.NotProgressing]: 'Not progressing',
      [CandidateStatus.Review]: 'In review',
      [CandidateStatus.InterviewStage_1]: '1st interview stage',
      [CandidateStatus.InterviewStage_2]: '2nd interview stage',
      [CandidateStatus.Offer]: 'Offer'
    };

    // Count candidates for each status
    const statusCounts: Record<CandidateStatus, number> = {
      [CandidateStatus.ShortListed]: 0,
      [CandidateStatus.Rejected]: 0,
      [CandidateStatus.Progressing]: 0,
      [CandidateStatus.NotProgressing]: 0,
      [CandidateStatus.Review]: 0,
      [CandidateStatus.InterviewStage_1]: 0,
      [CandidateStatus.InterviewStage_2]: 0,
      [CandidateStatus.Offer]: 0
    };

    // Count actual candidates
    candidates.forEach((candidate) => {
      if (candidate.status && candidate.status in statusCounts) {
        statusCounts[candidate.status]++;
      }
    });

    // Create array with all statuses, including those with 0 count
    return Object.entries(statusCounts).map(([status, count]) => ({
      label: `${statusLabels[status as CandidateStatus]} (${count})`,
      value: status as CandidateStatus
    }));
  }, [candidates]);

  return {
    statusOptions,
    loading: isFetching,
    error: fetchError
  };
};
